/**
 * Console sidebar — grouped navigation with accent-tinted eyebrows and an
 * animated active-trail bar (point 12), a persisted collapse-to-icons mode
 * with tooltips (point 13), and an overlay drawer under lg with backdrop,
 * Escape-to-close, focus return and body scroll lock (point 17).
 */
import { useEffect, useRef, type RefObject } from 'react';
import { NavLink } from 'react-router-dom';
import { useI18n } from '@servicesai/ui';
import { Icon } from './elevated/icons.js';
import type { ConsoleNavGroup } from '../lib/navigation.js';
import { S } from '../strings.js';

export const SIDEBAR_MODE_KEY = 'sai.console.sidebar';

export type SidebarMode = 'expanded' | 'collapsed';

export function readSidebarMode(): SidebarMode {
  try {
    return localStorage.getItem(SIDEBAR_MODE_KEY) === 'collapsed' ? 'collapsed' : 'expanded';
  } catch {
    return 'expanded';
  }
}

interface SidebarBodyProps {
  groups: ConsoleNavGroup[];
  collapsed: boolean;
  brandInitials: string;
  brandName: string;
  onToggleCollapse?: () => void;
  /** Called after any navigation (drawer closes itself). */
  onNavigate?: () => void;
}

function SidebarBody({
  groups,
  collapsed,
  brandInitials,
  brandName,
  onToggleCollapse,
  onNavigate,
}: SidebarBodyProps) {
  const { l } = useI18n();
  return (
    <>
      <div aria-hidden className="dls-strip" />
      <div
        className={`flex items-center gap-3 border-b border-white/10 py-4 ${
          collapsed ? 'justify-center px-2' : 'px-5'
        }`}
      >
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-(--radius-control) bg-aegold-500 text-sm font-black text-aeblack-900"
          style={{ backgroundColor: 'var(--tenant-accent, var(--color-aegold-500))' }}
        >
          {brandInitials}
        </span>
        {!collapsed && (
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[13px] font-bold text-white">{brandName}</div>
            <div className="mt-0.5 text-[10px] text-aeblack-300">{l(S.ministry)}</div>
          </div>
        )}
      </div>
      <nav
        aria-label={l({ en: 'Console sections', ar: 'أقسام وحدة التحكم' })}
        className="flex-1 overflow-y-auto px-3 py-3"
      >
        {groups.map((group) => (
          <div key={group.id}>
            {collapsed ? (
              <div aria-hidden className="mx-2 my-3 h-px bg-white/10" />
            ) : (
              <div
                className="px-3 pt-4 pb-1.5 text-[10px] font-extrabold tracking-[0.16em] uppercase"
                style={{ color: `color-mix(in srgb, ${group.accent} 55%, #ffffff)` }}
              >
                {l(group.label)}
              </div>
            )}
            {group.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                title={collapsed ? l(item.label) : undefined}
                onClick={onNavigate}
                className={({ isActive }) =>
                  [
                    'relative flex items-center gap-2.5 rounded-(--radius-control) py-2 text-[13px] font-medium transition-colors',
                    collapsed ? 'justify-center px-0' : 'px-3',
                    isActive
                      ? 'bg-aeblack-800 text-white'
                      : 'text-aeblack-200 hover:bg-aeblack-800/60 hover:text-white',
                  ].join(' ')
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      aria-hidden
                      className={`absolute start-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-full transition-all duration-200 ${
                        isActive ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
                      }`}
                      style={{ backgroundColor: group.accent }}
                    />
                    <Icon name={item.icon} size={collapsed ? 18 : 16} className="shrink-0" />
                    {!collapsed && <span className="truncate">{l(item.label)}</span>}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div
        className={`flex border-t border-white/10 py-3.5 text-[10px] text-aeblack-300 ${
          collapsed ? 'flex-col items-center gap-2.5 px-2' : 'items-center justify-between px-5'
        }`}
      >
        <span className="rounded border border-aegold-500/40 bg-aegold-500/10 px-1.5 py-0.5 font-bold tracking-wide text-aegold-300 uppercase">
          {l(S.envSandbox)}
        </span>
        {!collapsed && <span>© 2026 MOEI</span>}
        {onToggleCollapse && (
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-label={l(collapsed ? { en: 'Expand sidebar', ar: 'توسيع الشريط الجانبي' } : { en: 'Collapse sidebar', ar: 'طي الشريط الجانبي' })}
            title={l(collapsed ? { en: 'Expand sidebar', ar: 'توسيع الشريط الجانبي' } : { en: 'Collapse sidebar', ar: 'طي الشريط الجانبي' })}
            className="grid h-8 w-8 cursor-pointer place-items-center rounded-lg text-aeblack-300 transition hover:bg-white/10 hover:text-white"
          >
            <Icon name={collapsed ? 'arrow-end' : 'arrow-start'} size={16} className="rtl:-scale-x-100" />
          </button>
        )}
      </div>
    </>
  );
}

/** Desktop sidebar (lg+) — sticky column with animated collapse width. */
export function ConsoleSidebar({
  groups,
  collapsed,
  onToggleCollapse,
  brandInitials,
  brandName,
}: {
  groups: ConsoleNavGroup[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  brandInitials: string;
  brandName: string;
}) {
  return (
    <aside
      className={`sticky top-0 z-40 hidden h-screen shrink-0 flex-col bg-aeblack-900 text-aeblack-100 transition-[width] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] lg:flex ${
        collapsed ? 'w-[4.25rem]' : 'w-60'
      }`}
    >
      <SidebarBody
        groups={groups}
        collapsed={collapsed}
        brandInitials={brandInitials}
        brandName={brandName}
        onToggleCollapse={onToggleCollapse}
      />
    </aside>
  );
}

/** Mobile drawer (under lg) — overlay with backdrop, Escape, focus return. */
export function SidebarDrawer({
  open,
  onClose,
  groups,
  brandInitials,
  brandName,
  triggerRef,
}: {
  open: boolean;
  onClose: () => void;
  groups: ConsoleNavGroup[];
  brandInitials: string;
  brandName: string;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const { l } = useI18n();
  const panelRef = useRef<HTMLElement>(null);
  const wasOpen = useRef(false);

  // Body scroll lock while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus moves into the drawer on open and returns to the trigger on close.
  useEffect(() => {
    if (open && !wasOpen.current) {
      panelRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    } else if (!open && wasOpen.current) {
      triggerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open, triggerRef]);

  return (
    <div
      className={`fixed inset-0 z-50 transition-[visibility] duration-300 lg:hidden ${
        open ? 'visible' : 'invisible pointer-events-none'
      }`}
    >
      <div
        aria-hidden
        className={`absolute inset-0 bg-aeblack-900/50 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={l({ en: 'Navigation menu', ar: 'قائمة التنقل' })}
        className={`absolute inset-y-0 start-0 flex w-72 max-w-[85vw] flex-col bg-aeblack-900 text-aeblack-100 shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          open ? 'translate-x-0' : 'ltr:-translate-x-full rtl:translate-x-full'
        }`}
      >
        <SidebarBody
          groups={groups}
          collapsed={false}
          brandInitials={brandInitials}
          brandName={brandName}
          onNavigate={onClose}
        />
      </aside>
    </div>
  );
}
